import { Injectable, NgZone } from "@angular/core";
import { Subject, Observable, ReplaySubject, BehaviorSubject } from "rxjs";
import { IConversationStrategy } from "../../core-ui/models/strategies/IConversationStrategy";
import { IApplication } from "../../core-ui/models/IApplication";
import { ApplicationService } from "../../core-ui/services/application.service";
import {
  ConversationFactory,
  IContactInfo,
  IMessage,
  IMember,
  IConversation,
  IConversationEventHandlers,
  IConversationAuthClient,
  IConversationAuthCode,
  IWidgetParams as IConfigOptions,
  ChatModeEnum,
} from "@auvious/integrations";
import { IScheduledAppointmentContact, ITicket } from "../../core-ui/models";
import {
  CLAIM_CUSTOMER_ID,
  ConversationTypeEnum,
  DEFAULT_APPOINTMENT_CHAT_MODE,
  NotificationTypeEnum,
} from "../../core-ui/core-ui.enums";
import {
  AppointmentRoutingTypeEnum,
  IAppointment,
  IAppointmentRouting,
  IAppointmentRoutingGenesysCloudFlow,
  IAppointmentRoutingGenesysCloudQueue,
  IAppointmentRoutingTalkdeskDC,
} from "../../core-ui/models/Appointment";
import { debugError } from "../app.utils";
import { NotificationService } from "../../core-ui/services/notification.service";
import { TranslateService } from "@ngx-translate/core";
import { ConversationNotification } from "../../core-ui/models/notifications/ConversationNotification";
import {
  CUSTOMER_CALL_ACCEPTED_ACK,
  KEY_APPOINTMENT_CONVERSATION,
  QUEUE_TIMEOUT_MESSAGE,
} from "../app.enums";
import { sessionStore } from "@auvious/utils";
import { IInteraction } from "../../core-ui/models/IInteraction";
import {
  AuviousRtcService,
  GenericErrorHandler,
  UserService,
} from "../../core-ui";
import { DigitalConnectAuthClient, NoAuthClient } from "../models/";

const KEY_AGENT_NOTIFIED = "auvious.appointment.conversation.notified";
const KEY_AGENT_JOINED = "auvious.appointment.conversation.joined";

interface ICXoneAuthCode extends IConversationAuthCode {
  code: string;
}

interface IAppointmentConversation extends IConversation {
  ticket: string;
}

@Injectable()
export class ConversationService implements IConversationStrategy {
  private readySubject: Subject<void> = new Subject();
  private connectingSubject: ReplaySubject<void> = new ReplaySubject();
  private connectedSubject: ReplaySubject<void> = new ReplaySubject();
  private disconnectedSubject: Subject<void> = new Subject();
  private reconnectedSubject: Subject<void> = new Subject();
  private startedSubject: BehaviorSubject<IConversation> = new BehaviorSubject(
    undefined
  );
  private endedSubject: ReplaySubject<void> = new ReplaySubject();
  private messageSentSubject: Subject<IMessage> = new Subject();
  private messageReceivedSubject: ReplaySubject<IMessage> = new ReplaySubject();
  private agentNotififiedSubject: Subject<void> = new Subject();
  private agentJoinedSubject: Subject<IMember> = new Subject();
  private agentLeftSubject: Subject<void> = new Subject();
  private customerLeftSubject: Subject<void> = new Subject();
  private roomCreatedSubject: Subject<string> = new Subject();
  private agentTypingStartedSubject: Subject<void> = new Subject();
  private agentTypingStoppedSubject: Subject<void> = new Subject();
  private errorSubject: Subject<any> = new Subject();

  private conversationPanelToggledSubject: Subject<boolean> = new Subject();

  private keepConversationAliveInterval = 1000 * 60 * 5; // 5 minutes
  private keepaliveInterval: NodeJS.Timeout;
  private agent: IMember;
  private authClient: IConversationAuthClient;
  private isPanelOpen: boolean;
  private config: IConfigOptions;
  private reconnectTimeout: NodeJS.Timeout | number;
  private conversationData: IConversation;

  constructor(
    private applicationService: ApplicationService,
    private notification: NotificationService,
    private rtc: AuviousRtcService,
    private zone: NgZone,
    private translate: TranslateService,
    private errorHandler: GenericErrorHandler,
    private userService: UserService
  ) {}

  private get application(): IApplication {
    return this.applicationService.getActiveApplication();
  }

  private get impl(): IConversationStrategy {
    return this.application.conversationStrategy();
  }

  public get ready$(): Observable<void> {
    return this.readySubject.asObservable();
  }
  public get connecting$(): Observable<void> {
    return this.connectingSubject.asObservable();
  }
  public get connected$(): Observable<void> {
    return this.connectedSubject.asObservable();
  }
  public get disconnected$(): Observable<void> {
    return this.disconnectedSubject.asObservable();
  }
  public get reconnected$(): Observable<void> {
    return this.reconnectedSubject.asObservable();
  }
  public get started$(): Observable<IConversation> {
    return this.startedSubject.asObservable();
  }
  public get ended$(): Observable<void> {
    return this.endedSubject.asObservable();
  }
  public get messageSent$(): Observable<IMessage> {
    return this.messageSentSubject.asObservable();
  }
  public get messageReceived$(): Observable<IMessage> {
    return this.messageReceivedSubject.asObservable();
  }
  public get agentNotifified$(): Observable<void> {
    return this.agentNotififiedSubject.asObservable();
  }
  public get agentJoined$(): Observable<IMember> {
    return this.agentJoinedSubject.asObservable();
  }
  public get agentLeft$(): Observable<void> {
    return this.agentLeftSubject.asObservable();
  }
  public get customerLeft$(): Observable<void> {
    return this.customerLeftSubject.asObservable();
  }
  public get roomCreated$(): Observable<string> {
    return this.roomCreatedSubject.asObservable();
  }
  public get agentTypingStarted$(): Observable<void> {
    return this.agentTypingStartedSubject.asObservable();
  }
  public get agentTypingStopped$(): Observable<void> {
    return this.agentTypingStoppedSubject.asObservable();
  }
  public get error$(): Observable<void> {
    return this.errorSubject.asObservable();
  }
  public get conversationPanelToggled$(): Observable<boolean> {
    return this.conversationPanelToggledSubject.asObservable();
  }
  public get chatMode(): ChatModeEnum {
    return this.config.chatMode;
  }

  /**
   *
   * @param config: Configuration containing the properties needed to start a chat
   * @param ticketCode: the ticket found in the appointment link
   * @returns
   */
  public init(config: IConfigOptions, ticketCode: string): Promise<void> {
    this.config = config;
    return new Promise((resolve) => {
      const handlers: IConversationEventHandlers = {
        ready: () => {
          this.zone.run((_) => resolve());
        },
        connecting: (event: any) => {
          this.zone.run((_) => this.connectingSubject.next());
        },
        connected: (event: any) => {
          this.zone.run((_) => this.connectedSubject.next());
        },
        disconnected: (event: any) => {
          this.zone.run((_) => this.disconnectedSubject.next());
        },
        reconnected: (event: any) => {
          this.zone.run((_) => this.reconnectedSubject.next());
        },
        started: (conversation: IConversation) => {
          // this.clearReconnectTimeout();
          this.conversationData = conversation;
          this.setReconnectData(conversation, ticketCode);
          this.zone.run((_) => {
            ConversationFactory.instance.$conversation = conversation;
            // ConversationFactory.instance.sendMessage(
            //   this.translate.instant("appointment-notice-message"),
            //   "notice"
            // );
            this.startedSubject.next(conversation);
          });
        },
        ended: (event: any) => {
          this.zone.run((_) => {
            // this.notification.info("Conversation ended", {
            //   body: "You can no longer send or receive chat messages.",
            //   ttl: 5,
            // });
            this.resetConversation();
            this.endedSubject.next();
          });
        },
        messageSent: (message: IMessage) => {
          this.zone.run((_) => this.messageSentSubject.next(message));
        },
        messageReceived: (message: IMessage) => {
          this.zone.run((_) => {
            // discard auvious notices
            if (
              message.text &&
              (message.text.includes(
                this.translate.instant("appointment-notice-message")
              ) ||
                message.text.includes(this.translate.instant("chat-notice")) ||
                message.text.includes(
                  this.translate.instant("chat-notice-voice")
                ) ||
                message.text.includes(
                  this.translate.instant("chat-customer-rejected")
                ) ||
                message.text.includes("auvious:conversationId") ||
                // this is a substring. we don't know the actual customer's language
                // not sent for now
                message.text.includes(
                  this.translate.instant("Customer's language is")
                ) ||
                // discard ACK message
                message.text.includes(CUSTOMER_CALL_ACCEPTED_ACK) ||
                message.text.includes(QUEUE_TIMEOUT_MESSAGE) ||
                message.text.includes(
                  this.translate.instant("Customer's name is")
                ) ||
                message.text.includes(
                  this.translate.instant("Consent not given")
                ))
            ) {
              return;
            }

            this.handleMessageReceived(message);
            this.messageReceivedSubject.next(message);
          });
        },
        agentNotified: () => {
          this.zone.run((_) => {
            if (
              !sessionStore.getItem(KEY_AGENT_NOTIFIED) &&
              this.isConversationStarted
            ) {
              ConversationFactory.instance.sendMessage(
                this.translate.instant("appointment-notice-message"),
                "notice"
              );
              sessionStore.setItem(
                KEY_AGENT_NOTIFIED,
                new Date().toISOString()
              );
            }
            this.agentNotififiedSubject.next();
          });
        },
        agentJoined: (member: IMember) => {
          this.zone.run((_) => {
            this.agent = member;
            this.keepAliveConversation();
            this.agentJoinedSubject.next(member);
          });
        },
        agentLeft: () => {
          this.zone.run((_) => this.agentLeftSubject.next());
        },
        customerLeft: () => {
          this.zone.run((_) => this.customerLeftSubject.next());
        },
        roomCreated: (url: string) => {
          this.zone.run((_) => this.roomCreatedSubject.next(url));
        },
        cobrowseRequested: (ticket: string) => {},
        agentTypingStarted: () => {
          this.zone.run((_) => this.agentTypingStartedSubject.next());
        },
        agentTypingStopped: () => {
          this.zone.run((_) => this.agentTypingStoppedSubject.next());
        },
        error: (event: any) => {
          this.zone.run((_) => this.errorSubject.next(event));
        },
        messageError: (event: any) => {},
        memberSet: (member: IMember) => {},
        memberRemoved: (member: IMember) => {},
      };

      switch (config.chatMode) {
        case "talkdesk-digital-connect":
          this.authClient = new DigitalConnectAuthClient(
            this.rtc,
            this.userService
          );
          break;
        default:
          this.authClient = new NoAuthClient();
          break;
      }

      ConversationFactory.create(
        config,
        handlers,
        this.rtc.getAuviousCommonClient(),
        this.authClient
      );
    });
  }

  private keepAliveConversation() {
    this.keepaliveInterval = setInterval(async () => {
      try {
        await ConversationFactory.instance.sendTyping();
      } catch (ex) {
        debugError(ex);
      }
    }, this.keepConversationAliveInterval);
  }

  private clearKeepAlive() {
    if (!!this.keepaliveInterval) clearInterval(this.keepaliveInterval);
  }

  public resetConversation() {
    this.agent = null;
    this.notification.dismissAll();
    this.clearKeepAlive();
    this.clearSession();
    ConversationFactory.instance?.destroy();
  }

  private clearSession() {
    sessionStore.removeItem(KEY_APPOINTMENT_CONVERSATION);
    sessionStore.removeItem(KEY_AGENT_NOTIFIED);
    sessionStore.removeItem(KEY_AGENT_JOINED);
    this.conversationData = undefined;
  }

  private clearReconnectTimeout() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  public setConversationOriginCustomer(appointmentId: string) {
    sessionStore.setItem(
      `auvious.appointment.${appointmentId}`,
      this.userService.getActiveUser().getClaim(CLAIM_CUSTOMER_ID)
    );
  }

  public isConversationOriginCustomer(appointmentId: string) {
    return (
      sessionStore.getItem(`auvious.appointment.${appointmentId}`) ===
      this.userService.getActiveUser().getClaim(CLAIM_CUSTOMER_ID)
    );
  }

  public clearConversationOrigin(appointmentId: string) {
    sessionStore.removeItem(`auvious.appointment.${appointmentId}`);
  }

  public setAgentJoined(appointentId: string) {
    sessionStore.setItem(KEY_AGENT_JOINED, appointentId);
  }

  public isAgentAlreadyJoined(appointmentId: string) {
    return sessionStore.getItem(KEY_AGENT_JOINED) === appointmentId;
  }

  // public

  private handleMessageReceived(message: IMessage) {
    if (this.isPanelOpen) {
      return;
    }

    let notif: ConversationNotification;
    let title: string;

    switch (message.type) {
      case "file":
        title = "File received";
        // notif = new FileTransferNotification({
        //   id: message.id,
        //   targetId: "",
        //   targetType: "conference",
        //   mimeType: "",
        //   userId: "",
        //   filename: message.file.name,
        //   sentAt: message.timestamp,
        //   signedUrl: new Observable((obs) => {
        //     ConversationFactory.instance.downloadFile(message.file);
        //     obs.next("");
        //   }),
        // });
        notif = new ConversationNotification(
          title,
          message.file?.name,
          message.id
        );
        break;
      case "text":
        title = this.agent?.displayName || "Message";
        notif = new ConversationNotification(title, message.text, message.id);
        break;
      case "bot":
        title = this.agent?.displayName || "Bot";
        notif = new ConversationNotification(title, message.text, message.id);
        break;
    }

    if (notif) {
      notif.onClicked(() => {
        this.toggleConversationPanel(true);
      });
      this.notification.notify(notif);
    }
  }

  public toggleConversationPanel(open: boolean) {
    this.isPanelOpen = open;
    if (this.isPanelOpen) {
      this.notification.dismissAll(NotificationTypeEnum.conversation);
    }
    this.conversationPanelToggledSubject.next(open);
  }

  /** check if conversation has started (we got conversation data) */
  public get isConversationStarted(): boolean {
    return !!ConversationFactory.instance?.$conversation;
  }

  /** check if the instance has been initialised */
  public get isConversationInitialised(): boolean {
    try {
      return !!ConversationFactory.instance;
    } catch (ex) {
      return false;
    }
  }

  /** Applies only to customer */

  // public isOriginCustomer() {
  //   const str = sessionStore.getItem(KEY_APPOINTMENT_CONVERSATION);
  //   if (!str) {
  //     return false;
  //   }
  //   try {
  //     const conversation: IConversation = JSON.parse(str);
  //     return (
  //       !!conversation.id && conversation?.id === this.conversationData?.id
  //     );
  //   } catch (ex) {
  //     return false;
  //   }
  // }

  private setReconnectData(conversation: IConversation, ticket: string) {
    sessionStore.setItem(
      KEY_APPOINTMENT_CONVERSATION,
      JSON.stringify({
        ...conversation,
        ticket,
      } as IAppointmentConversation)
    );
  }

  public isReconnect(ticket: string) {
    const dataStr = sessionStore.getItem(KEY_APPOINTMENT_CONVERSATION);
    if (!dataStr) return false;
    try {
      const data: IAppointmentConversation = JSON.parse(dataStr);
      return data.ticket === ticket;
    } catch (ex) {
      return false;
    }
  }

  public async connect(
    contact: IContactInfo,
    ticketCode: string
  ): Promise<void> {
    if (this.isReconnect(ticketCode)) {
      return ConversationFactory.instance.reconnect(
        contact,
        ConversationTypeEnum.videoCall
      );
    }
    this.clearSession();
    let code: IConversationAuthCode;
    switch (this.config.chatMode) {
      case "talkdesk-digital-connect":
        code = (this.authClient as DigitalConnectAuthClient).getAuthCode(
          contact,
          this.config.dcTouchpointId
        );
        break;
      case "genesys-web-messaging":
        code = { applicationId: this.application.getId() };
        break;
      case "cxone-dfo-chat":
        code = {
          applicationId: this.application.getId(),
          code: this.config.cxAuthorizationCode,
        } as ICXoneAuthCode;
        break;
    }
    return ConversationFactory.instance.createChat(
      contact,
      ConversationTypeEnum.videoCall,
      code
    );
  }

  /** Applies only to customer */
  public endConversation(): Promise<void> {
    try {
      return ConversationFactory.instance.endChat();
    } catch (ex) {
      // do nothing
    }
  }

  /** as Agent */
  public leaveConversation(interaction: IInteraction): Promise<void> {
    return this.impl.leaveConversation(interaction);
  }

  /** as agent */
  public holdConversation(interaction: IInteraction) {
    return this.impl.holdConversation(interaction);
  }

  public getMessages(interaction: IInteraction): Promise<IMessage[]> {
    return this.impl.getMessages(interaction);
  }

  /** Applies only to customer */
  public sendMessage(
    message: string,
    bodyType?: "notice" | "standard"
  ): Promise<void> {
    return ConversationFactory.instance.sendMessage(message, bodyType);
  }

  public prepareConversationRequest(
    ticket: ITicket,
    appointment: IAppointment<IAppointmentRouting>,
    contact: IScheduledAppointmentContact
  ): IContactInfo {
    return this.impl.prepareConversationRequest(ticket, appointment, contact);
  }

  public prepareConversationConfig(
    ticket: ITicket,
    appointment: IAppointment<IAppointmentRouting>
  ): IConfigOptions {
    return this.impl.prepareConversationConfig(ticket, appointment);
  }

  public getConversationChatMode(
    appointment: IAppointment<
      | IAppointmentRouting
      | IAppointmentRoutingGenesysCloudFlow
      | IAppointmentRoutingTalkdeskDC
    >
  ): ChatModeEnum {
    let chatMode: ChatModeEnum;
    let queueName = (
      appointment.interaction.routing as IAppointmentRoutingGenesysCloudQueue
    ).properties?.queueName;

    switch (appointment.interaction.routing.type) {
      case AppointmentRoutingTypeEnum.GENESYS_CLOUD_FLOW:
        chatMode = (appointment.interaction.routing.properties.chatMode ||
          appointment.metadata?.chatMode) as ChatModeEnum;

        if (chatMode === "ai-chat") {
          chatMode = !!queueName ? "genesys-cloud" : "genesys-web-messaging";
        }
        break;
      case AppointmentRoutingTypeEnum.GENESYS_CLOUD_QUEUE:
        chatMode = "genesys-cloud";
        break;
      case AppointmentRoutingTypeEnum.TALKDESK_DIGITAL_CONNECT:
        chatMode = "talkdesk-digital-connect";
        break;
      default:
        chatMode = DEFAULT_APPOINTMENT_CHAT_MODE;
        break;
    }

    return chatMode;
  }
}
